import { Component, inject, input, output, signal } from '@angular/core';
import { CdkDragDrop, DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop';
import { MediaService, type MediaAssetDto } from '../../../../../../shared/services/media.service';

@Component({
  selector: 'app-release-image-uploader',
  standalone: true,
  imports: [DragDropModule],
  templateUrl: './release-image-uploader.component.html',
  styleUrl: './release-image-uploader.component.scss',
})
export class ReleaseImageUploaderComponent {
  private readonly media = inject(MediaService);

  readonly imageUrls = input<string[]>([]);
  readonly imageUrlsChange = output<string[]>();

  readonly dragging = signal(false);
  readonly uploading = signal(false);
  readonly uploadError = signal<string | null>(null);

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    this.dragging.set(true);
  }

  onDragLeave(): void {
    this.dragging.set(false);
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    this.dragging.set(false);
    const files = event.dataTransfer?.files;
    if (files?.length) {
      this.uploadFiles(Array.from(files));
    }
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files?.length) {
      this.uploadFiles(Array.from(input.files));
      input.value = '';
    }
  }

  replaceAt(index: number, event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    this.uploading.set(true);
    this.media.upload(file).subscribe({
      next: (asset: MediaAssetDto) => {
        const next = [...this.imageUrls()];
        next[index] = asset.url;
        this.imageUrlsChange.emit(next);
        this.uploading.set(false);
        input.value = '';
      },
      error: () => {
        this.uploadError.set('Image upload failed.');
        this.uploading.set(false);
        input.value = '';
      },
    });
  }

  removeAt(index: number): void {
    const next = this.imageUrls().filter((_, i) => i !== index);
    this.imageUrlsChange.emit(next);
  }

  onReorder(event: CdkDragDrop<string[]>): void {
    const next = [...this.imageUrls()];
    moveItemInArray(next, event.previousIndex, event.currentIndex);
    this.imageUrlsChange.emit(next);
  }

  private uploadFiles(files: File[]): void {
    const images = files.filter((f) => f.type.startsWith('image/'));
    if (images.length === 0) {
      this.uploadError.set('Please select image files only.');
      return;
    }
    this.uploading.set(true);
    this.uploadError.set(null);
    const results = [...this.imageUrls()];
    let index = 0;

    const uploadNext = (): void => {
      if (index >= images.length) {
        this.imageUrlsChange.emit(results);
        this.uploading.set(false);
        return;
      }
      const file = images[index];
      index += 1;
      this.media.upload(file).subscribe({
        next: (asset: MediaAssetDto) => {
          results.push(asset.url);
          uploadNext();
        },
        error: () => {
          this.uploadError.set('One or more uploads failed.');
          this.uploading.set(false);
        },
      });
    };

    uploadNext();
  }
}
